import { useCallback, useEffect, useReducer, useRef } from "react";
import { useApi } from "../contexts/ApiContext";
import { putToGcs } from "./putToGcs";
import { runWithConcurrency } from "./runWithConcurrency";
import type { components } from "../api/types";
import type {
  UploadFilter,
  UploadIntent,
  UploadItem,
  UploadSession,
  UploadState,
} from "./types";

type StartBody = components["schemas"]["StartUploadRequest"] & { existing_file_ids?: number[] };

const POLL_INITIAL_INTERVAL_MS = 2000;
const POLL_MAX_INTERVAL_MS = 10000;
const PUT_CONCURRENCY = 4;

type Action =
  | { type: "START_INIT" }
  | { type: "START_OK"; session: UploadSession; items: UploadItem[]; submittedOriginalNames: ReadonlySet<string> }
  | { type: "PUT_PROGRESS"; itemId: string; bytes: number; total: number }
  | { type: "PUT_FAILED"; itemId: string }
  | { type: "POLL_RESULT"; session: UploadSession; items: UploadItem[] }
  | { type: "RETRY_INIT"; itemId: string }
  | { type: "REMOVE_ITEM"; itemId: string }
  | { type: "RESET" }
  | { type: "ERROR"; error: string };

const EMPTY_SET: ReadonlySet<string> = new Set();

const initialState: UploadState = {
  session: null,
  items: [],
  progressByItemId: {},
  submittedOriginalNames: EMPTY_SET,
  isPolling: false,
  isStarting: false,
  error: null,
};

function itemsFromView(raw: any[]): UploadItem[] {
  return raw.map((i) => ({
    item_id: i.item_id,
    parent_item_id: i.parent_item_id ?? null,
    origin: i.origin,
    filename: i.filename,
    status: i.status,
    error_code: i.error_code ?? undefined,
    error_message: i.error_message ?? undefined,
    action_item: i.action_item ?? undefined,
    error_effect: i.error_effect ?? undefined,
    details: i.details ?? undefined,
    bytes_uploaded: i.bytes_uploaded ?? undefined,
  })) as UploadItem[];
}

function sessionFromView(raw: any): UploadSession {
  return {
    upload_id: raw.upload_id,
    status: raw.status,
    intent: raw.intent,
    case_id: raw.case_id ?? null,
    revision_id: raw.revision_id ?? null,
    error_code: raw.error_code ?? null,
    error_message: raw.error_message ?? null,
    action_item: raw.action_item ?? null,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    submitted_at: raw.submitted_at ?? null,
    completed_at: raw.completed_at ?? null,
  };
}

function reducer(state: UploadState, action: Action): UploadState {
  switch (action.type) {
    case "START_INIT":
      return { ...initialState, isStarting: true };

    case "START_OK":
      return {
        ...state,
        session: action.session,
        items: action.items,
        submittedOriginalNames: action.submittedOriginalNames,
        error: null,
        isStarting: false,
        progressByItemId: {},
      };

    case "PUT_PROGRESS": {
      const pct = action.total > 0 ? Math.round((action.bytes / action.total) * 50) : 0;
      return {
        ...state,
        progressByItemId: { ...state.progressByItemId, [action.itemId]: pct },
      };
    }

    case "POLL_RESULT":
      return {
        ...state,
        session: action.session,
        items: action.items,
        isPolling: action.session.status !== "COMPLETED" && action.session.status !== "FAILED",
        // Map server bytes_uploaded → 50-100% range for processing phase.
        // Seed from existing progress so items absent from this poll response are not zeroed.
        progressByItemId: action.items.reduce<Record<string, number>>((acc, item) => {
          const current = state.progressByItemId[item.item_id] ?? 0;
          if (item.status === "PROCESSING" || item.status === "UPLOADED") {
            acc[item.item_id] = Math.max(current, 50);
          } else if (item.status === "ACCEPTED" || item.status === "REJECTED") {
            acc[item.item_id] = 100;
          } else {
            acc[item.item_id] = current;
          }
          return acc;
        }, { ...state.progressByItemId }),
      };

    case "PUT_FAILED": {
      return {
        ...state,
        items: state.items.map((item) => {
          if (item.item_id !== action.itemId) return item;
          return {
            item_id: item.item_id,
            parent_item_id: item.parent_item_id,
            origin: item.origin,
            filename: item.filename,
            status: "REJECTED",
            error_code: "UPLOAD_FAILED",
            error_effect: "RETRY",
            error_message: "File could not be uploaded to storage",
            action_item: "Check your connection and try again.",
          } as UploadItem;
        }),
        progressByItemId: { ...state.progressByItemId, [action.itemId]: 0 },
      };
    }

    case "RETRY_INIT":
      return {
        ...state,
        items: state.items.map((item) =>
          item.item_id === action.itemId
            ? ({ ...item, status: "REQUESTED" } as UploadItem)
            : item
        ),
        progressByItemId: { ...state.progressByItemId, [action.itemId]: 0 },
      };

    case "REMOVE_ITEM": {
      const { [action.itemId]: _removed, ...restProgress } = state.progressByItemId;
      return {
        ...state,
        items: state.items.filter((i) => i.item_id !== action.itemId && i.parent_item_id !== action.itemId),
        progressByItemId: restProgress,
      };
    }

    case "RESET":
      return { ...initialState };

    case "ERROR":
      return { ...state, error: action.error, isPolling: false, isStarting: false };

    default:
      return state;
  }
}

export interface UseUploadResult extends UploadState {
  /** True while a session is active and non-terminal (starting, uploading, or processing). */
  isRunning: boolean;
  start: (opts: { intent: UploadIntent; case_id?: string; files: File[]; existing_file_ids?: number[] }) => Promise<void>;
  retryItem: (itemId: string) => Promise<void>;
  removeItem: (itemId: string) => void;
  reset: () => void;
}

export function useUpload(): UseUploadResult {
  const { client } = useApi();
  const [state, dispatch] = useReducer(reducer, initialState);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef(POLL_INITIAL_INTERVAL_MS);
  const lastItemStatusesRef = useRef<string>("");
  const uploadIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  // Incremented on every new session (start) and on reset to invalidate stale poll callbacks.
  const sessionGenRef = useRef(0);
  // Original File objects stored by item_id so retryItem can re-upload without re-selecting.
  const fileByItemIdRef = useRef<Map<string, File>>(new Map());
  // Synchronous lock preventing concurrent start() invocations (guards the double-click window
  // before isStarting:true propagates through a render cycle).
  const isStartingRef = useRef(false);
  // Tracks in-flight retries to prevent duplicate GCS PUTs for the same item.
  const retryingItemIdsRef = useRef<Set<string>>(new Set());
  // Aborts all in-flight XHR PUTs for the current session (reset, unmount).
  const putAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      putAbortControllerRef.current?.abort();
    };
  }, []);

  // Cancel any in-flight poll and invalidate its generation before dispatching ERROR,
  // preventing a stale poll callback from overwriting the error state.
  const dispatchError = useCallback((error: string) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    sessionGenRef.current += 1;
    dispatch({ type: "ERROR", error });
  }, []);

  const schedulePoll = useCallback((uploadId: string) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    // Capture generation at schedule time — if reset() fires before this callback
    // executes, the generation mismatch prevents stale dispatches.
    const gen = sessionGenRef.current;
    pollTimerRef.current = setTimeout(async () => {
      if (gen !== sessionGenRef.current || !isMountedRef.current) return;
      try {
        const res = await client.GET("/v2/uploads/{upload_id}", {
          params: { path: { upload_id: uploadId } },
        });
        if (gen !== sessionGenRef.current || !isMountedRef.current) return;
        if (res.error || !res.data) {
          console.error("[upload]", uploadId, "poll error", res.error);
          // Re-arm so the loop doesn't silently die on transient errors.
          if (isMountedRef.current) schedulePoll(uploadId);
          return;
        }
        const session = sessionFromView(res.data);
        const items = itemsFromView(res.data.items ?? []);

        // Detect if any item changed status — if so, reset backoff
        const statusSnapshot = items.map((i) => `${i.item_id}:${i.status}`).join(",");
        if (statusSnapshot !== lastItemStatusesRef.current) {
          pollIntervalRef.current = POLL_INITIAL_INTERVAL_MS;
          lastItemStatusesRef.current = statusSnapshot;
        } else {
          pollIntervalRef.current = Math.min(
            pollIntervalRef.current * 2,
            POLL_MAX_INTERVAL_MS
          );
        }

        dispatch({ type: "POLL_RESULT", session, items });
        console.info("[upload]", uploadId, "poll →", session.status);

        if (session.status !== "COMPLETED" && session.status !== "FAILED") {
          if (isMountedRef.current) schedulePoll(uploadId);
        }
      } catch (e) {
        console.error("[upload]", uploadId, "poll exception", e);
        if (gen === sessionGenRef.current && isMountedRef.current) schedulePoll(uploadId);
      }
    }, pollIntervalRef.current);
  }, [client]);

  const start = useCallback(
    async (opts: { intent: UploadIntent; case_id?: string; files: File[]; existing_file_ids?: number[] }) => {
      // Synchronous guard: prevents a second invocation from racing the first
      // before the isStarting:true state change reaches the UI.
      if (isStartingRef.current) return;
      isStartingRef.current = true;

      putAbortControllerRef.current?.abort();
      const putController = new AbortController();
      putAbortControllerRef.current = putController;

      dispatch({ type: "START_INIT" });
      // New session invalidates any polls from a prior session.
      sessionGenRef.current += 1;
      const gen = sessionGenRef.current;
      pollIntervalRef.current = POLL_INITIAL_INTERVAL_MS;
      lastItemStatusesRef.current = "";

      try {
        const res = await client.POST("/v2/uploads/start", {
          body: {
            intent: opts.intent,
            case_id: opts.case_id ?? null,
            files: opts.files.map((f) => ({
              filename: f.name,
              size: f.size,
              content_type: f.type || null,
            })),
            ...(opts.existing_file_ids?.length ? { existing_file_ids: opts.existing_file_ids } : {}),
          } as StartBody,
        });

        if (!isMountedRef.current || gen !== sessionGenRef.current) return;

        if (res.error || !res.data) {
          const status = res.response?.status ?? 0;
          if (status >= 500 || status === 0) {
            dispatchError("Upload service is unavailable. Try again in a minute.");
          } else {
            dispatchError("Could not start upload. Please check your files and try again.");
          }
          return;
        }

        const { upload_id, items: signedItems } = res.data;
        uploadIdRef.current = upload_id;

        // Store files by item_id for retry. Matched by index — server returns signedItems
        // in the same order files were submitted. Filename-based lookup is unreliable because
        // the server sanitizes filenames (e.g. werkzeug.secure_filename strips '+', spaces → '_').
        fileByItemIdRef.current = new Map(
          signedItems.map((si, idx) => [si.item_id, opts.files[idx]])
        );
        if (process.env.NODE_ENV !== "production") {
          signedItems.forEach((si, idx) => {
            const f = opts.files[idx];
            const sanitized = f?.name.replace(/[^a-zA-Z0-9._-]/g, "_").toLowerCase();
            if (f && si.filename !== f.name && si.filename !== sanitized) {
              console.warn("[upload] index mismatch at", idx, "— server:", si.filename, "local:", f.name);
            }
          });
        }

        // Build a minimal session — real session data arrives via polling
        const pseudoSession: UploadSession = {
          upload_id,
          status: "OPEN",
          intent: opts.intent,
          case_id: opts.case_id ?? null,
          revision_id: null,
          error_code: null,
          error_message: null,
          action_item: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          submitted_at: null,
          completed_at: null,
        };

        const initialItems: UploadItem[] = signedItems.map((si) => ({
          item_id: si.item_id,
          parent_item_id: null,
          origin: "DIRECT" as const,
          filename: si.filename,
          status: "REQUESTED" as const,
        }));

        dispatch({ type: "START_OK", session: pseudoSession, items: initialItems, submittedOriginalNames: new Set(opts.files.map((f) => f.name)) });
        console.info("[upload]", upload_id, "started, items:", signedItems.length);

        const putFailedIds: string[] = [];

        await runWithConcurrency(
          signedItems.map((si, idx) => async () => {
            const file = opts.files[idx];
            if (!file) {
              console.error("[upload] no file at index", idx, "for item", si.item_id, si.filename);
              putFailedIds.push(si.item_id);
              dispatch({ type: "PUT_FAILED", itemId: si.item_id });
              return;
            }
            try {
              await putToGcs(si.signed_put_url, file, si.put_headers, (bytes, total) => {
                dispatch({ type: "PUT_PROGRESS", itemId: si.item_id, bytes, total });
              }, { signal: putController.signal });
              console.info("[upload]", upload_id, "item PUT done", si.item_id);
            } catch (e) {
              if (e instanceof DOMException && e.name === "AbortError") return;
              console.error("[upload]", upload_id, "item PUT failed", si.item_id, e);
              putFailedIds.push(si.item_id);
              dispatch({ type: "PUT_FAILED", itemId: si.item_id });
            }
          }),
          PUT_CONCURRENCY
        );

        if (!isMountedRef.current || gen !== sessionGenRef.current) return;

        // Always call /finish even on partial PUT failures. Items that failed to PUT are already
        // marked REJECTED client-side via PUT_FAILED; /finish lets the server process the items
        // that did upload successfully rather than orphaning the session in OPEN state.
        if (putFailedIds.length === signedItems.length) {
          // Every single PUT failed — no point calling /finish, server has nothing to process.
          const n = putFailedIds.length;
          dispatchError(`Failed to upload ${n} file${n === 1 ? "" : "s"} to storage. Check your connection and try again.`);
          return;
        }

        const finishRes = await client.POST("/v2/uploads/{upload_id}/finish", {
          params: { path: { upload_id } },
          body: undefined as any,
        });

        if (!isMountedRef.current || gen !== sessionGenRef.current) return;

        if (finishRes.error || !finishRes.data) {
          console.error("[upload]", upload_id, "finish failed", finishRes.error);
          dispatchError("Failed to submit upload for processing. Please try again.");
          return;
        }

        console.info("[upload]", upload_id, "finish →", finishRes.data.status);
        schedulePoll(upload_id);
      } finally {
        isStartingRef.current = false;
      }
    },
    [client, schedulePoll, dispatchError]
  );

  const retryItem = useCallback(
    async (itemId: string) => {
      // Prevent concurrent retries for the same item (e.g. row button + card button both visible).
      if (retryingItemIdsRef.current.has(itemId)) return;
      retryingItemIdsRef.current.add(itemId);

      try {
        const uploadId = uploadIdRef.current;
        if (!uploadId) return;

        const file = fileByItemIdRef.current.get(itemId);
        if (!file) {
          console.error("[upload] no file available for retry", itemId);
          return;
        }

        dispatch({ type: "RETRY_INIT", itemId });

        const res = await client.POST("/v2/uploads/{upload_id}/items/{item_id}/retry", {
          params: { path: { upload_id: uploadId, item_id: itemId } },
          body: undefined as any,
        });

        if (res.error || !res.data) {
          console.error("[upload]", uploadId, "retry failed for item", itemId, res.error);
          dispatch({ type: "PUT_FAILED", itemId });
          return;
        }

        const { signed_put_url, put_headers } = res.data;
        console.info("[upload]", uploadId, "retry PUT start", itemId);

        try {
          await putToGcs(signed_put_url, file, put_headers, (bytes, total) => {
            dispatch({ type: "PUT_PROGRESS", itemId, bytes, total });
          }, { signal: putAbortControllerRef.current?.signal });
          console.info("[upload]", uploadId, "retry PUT done", itemId);
        } catch (e) {
          if (e instanceof DOMException && e.name === "AbortError") return;
          console.error("[upload]", uploadId, "retry PUT failed", itemId, e);
          dispatch({ type: "PUT_FAILED", itemId });
        }

        // Always re-arm polling — schedulePoll debounces via clearTimeout, so calling it
        // while already polling is safe. This also handles the case where polling had stopped
        // (e.g. session previously FAILED) and needs to resume after the retry.
        schedulePoll(uploadId);
      } finally {
        retryingItemIdsRef.current.delete(itemId);
      }
    },
    [client, schedulePoll]
  );

  const removeItem = useCallback((itemId: string) => {
    dispatch({ type: "REMOVE_ITEM", itemId });
  }, []);

  const reset = useCallback(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    putAbortControllerRef.current?.abort();
    putAbortControllerRef.current = null;
    // Invalidate any in-flight poll callbacks before clearing state.
    sessionGenRef.current += 1;
    uploadIdRef.current = null;
    fileByItemIdRef.current = new Map();
    pollIntervalRef.current = POLL_INITIAL_INTERVAL_MS;
    lastItemStatusesRef.current = "";
    dispatch({ type: "RESET" });
  }, []);

  const isRunning =
    state.isStarting ||
    (state.session !== null &&
      !state.error &&
      state.session.status !== "COMPLETED" &&
      state.session.status !== "FAILED");

  return { ...state, isRunning, start, retryItem, removeItem, reset };
}

export type { UploadFilter };
